import { forwardRef } from 'react';
import { TEXT_LINK_SIZES, TEXT_LINK_TYPES } from './TextLink.constants';
import { styles } from './TextLink.style';
import { TextLinkProps } from './TextLink.types';
import { BUTTON_TEXT_POSITIONS } from '@photosi/rn-components/buttons/button/Button.types.constants';
import Button from '../../../components/buttons/button/Button';
import { withPersonalizedTextLinkStyle } from './withPersonalizedTextLinkStyle';
import { TBaseButtonChildrenProps } from '@photosi/rn-components/buttons/base-button/BaseButton.types';
import { ButtonIconChild } from '@photosi/rn-components/buttons/base-button-children/button-icon-child/ButtonIconChild';
import { mergeStyles } from 'src/_shared/utils/mergeStyles';

const TEXT_LINK_SIZE_STYLES_MAP = {
  [TEXT_LINK_SIZES.SMALL]: {
    button: styles.buttonSmall,
    buttonInner: styles.buttonInnerSmall,
    icon: styles.iconSmall,
    text: styles.textSmall,
    textHover: styles.textHover,
    textDisabled: styles.textDisabled,
    iconHover: styles.iconHover,
    iconDisabled: styles.iconDisabled,
  },
  [TEXT_LINK_SIZES.DEFAULT]: {
    button: styles.buttonDefault,
    buttonInner: styles.buttonInnerDefault,
    icon: styles.iconDefault,
    text: styles.textDefault,
    textHover: styles.textHover,
    textDisabled: styles.textDisabled,
    iconHover: styles.iconHover,
    iconDisabled: styles.iconDisabled,
  },
};

const TextLinkComponent = forwardRef(({ size, type, iconName, iconTestID, ...buttonProps }: TextLinkProps, ref) => {
  const {
    style: userStyle,
    innerStyle: userInnerStyle,
    textStyle: userTextStyle,
    textHoverStyle: userTextHoverStyle,
    textDisabledStyle: userTextDisabledStyle,
    iconStyle: userIconStyle,
    iconHoverStyle: userIconHoverStyle,
    iconDisabledStyle: userIconDisabledStyle,
    accessibilityRole = 'link',
    ...restButtonProps
  } = buttonProps;
  const sizeStyles = TEXT_LINK_SIZE_STYLES_MAP[size];

  const buttonStyle = mergeStyles(sizeStyles?.button, userStyle);
  const buttonInnerStyle = mergeStyles(sizeStyles?.buttonInner, userInnerStyle);

  const textStyle = mergeStyles(sizeStyles?.text, userTextStyle);
  const textHoverStyle = mergeStyles(sizeStyles?.textHover, userTextHoverStyle);
  const textDisabledStyle = mergeStyles(sizeStyles?.textDisabled, userTextDisabledStyle);

  const iconStyle = mergeStyles(sizeStyles?.icon, userIconStyle);
  const iconHoverStyle = mergeStyles(sizeStyles?.iconHover, userIconHoverStyle);
  const iconDisabledStyle = mergeStyles(sizeStyles?.iconDisabled, userIconDisabledStyle);
  const textPosition = TEXT_LINK_TYPES.ICON_LEFT === type ? BUTTON_TEXT_POSITIONS.RIGHT : BUTTON_TEXT_POSITIONS.LEFT;

  return (
    <Button
      ref={ref}
      {...restButtonProps}
      accessibilityRole={accessibilityRole}
      style={buttonStyle}
      innerStyle={buttonInnerStyle}
      textStyle={textStyle}
      textPosition={textPosition}
      textHoverStyle={textHoverStyle}
      textPressedStyle={textHoverStyle}
      textDisabledStyle={textDisabledStyle}
    >
      {(baseButtonChildrenProps: TBaseButtonChildrenProps) => {
        return TEXT_LINK_TYPES.NO_ICON !== type ? (
          <ButtonIconChild
            {...{
              ...baseButtonChildrenProps,
              iconName,
              iconStyle: iconStyle,
              iconDisabledStyle: iconDisabledStyle,
              iconHoverStyle: iconHoverStyle,
              iconPressedStyle: iconHoverStyle,
              iconTestID,
            }}
          />
        ) : null;
      }}
    </Button>
  );
});

TextLinkComponent.displayName = 'TextLinkComponent';

export const TextLink = withPersonalizedTextLinkStyle(TextLinkComponent, styles);
